import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import HomeCreditService from "../../services/HomeCreditService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";

function dayLabel(status, cycleDay, coins) {
    const day = cycleDay >= 1 ? cycleDay : 1;
    const amount = Math.max(0, Number(coins) || 0);
    if (status === "claimed") return `Day ${day} claimed`;
    if (status === "repair") return "Keep your streak";
    return `Day ${day} · +${amount}`;
}

export default function JigsawDailyTreasure() {
    const [signedIn, setSignedIn] = useState(false);
    const [streak, setStreak] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        const data = await HomeCreditService.fetchJigsawStreak();
        setStreak(data);
    }, []);

    useEffect(() => {
        let cancel = false;
        UserService.getUserInfo()
            .then(async (info) => {
                if (cancel) return;
                if (!info?.cloudUser) {
                    setSignedIn(false);
                    return;
                }
                setSignedIn(true);
                try {
                    await load();
                } catch (err) {
                    if (!cancel) {
                        Logger.warn("Jigsaw daily coins:", err?.message || err);
                        setError(err?.message || "Could not load today's coins.");
                    }
                }
            })
            .catch(() => {
                if (!cancel) setSignedIn(false);
            });
        return () => {
            cancel = true;
        };
    }, [load]);

    const handleClaim = async () => {
        if (!streak || busy || streak.status === "claimed") return;
        setBusy(true);
        setError("");
        try {
            const data = await HomeCreditService.claimJigsawStreak({
                repair: streak.status === "repair" ? "grace" : undefined,
            });
            setStreak(data);
        } catch (err) {
            Logger.warn("Jigsaw daily claim:", err?.message || err);
            setError(err?.message || "Could not claim today's coins.");
        } finally {
            setBusy(false);
        }
    };

    if (!signedIn) return null;
    if (!streak && !error) return null;

    const status = streak.status || "ready";
    const balance = Math.max(0, Number(streak.creditBalance) || 0);
    const claimed = status === "claimed";

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2.5,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
            }}
        >
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Daily coins
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {balance} coins
                </Typography>
                {error ? (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                        {error}
                    </Typography>
                ) : null}
            </Box>
            <Button
                variant={claimed ? "outlined" : "contained"}
                disabled={claimed || busy}
                onClick={handleClaim}
                sx={{ textTransform: "none", minHeight: 44, minWidth: 160 }}
            >
                {busy ? "Claiming…" : dayLabel(status, streak.cycleDay, streak.coins)}
            </Button>
        </Paper>
    );
}
